/**
 * VK retargeting pixel — gate predicate + inject / teardown helpers.
 *
 * The pixel is loaded straight from the VENDOR'S CDN (`PIXEL_SRC` →
 * scripts.prdredir.com) as a plain, credentialed `<script>`. That is a
 * deliberate product-owner decision: the vendor requires their own CDN so the
 * `slfp` session cookie their attribution needs is set on the script fetch. It
 * is intentionally NOT self-hosted and NOT integrity-pinned — adding
 * `crossorigin`/`integrity` would turn this into an uncredentialed CORS fetch
 * and break `slfp` (their CORS is `Access-Control-Allow-Origin: *`, so
 * `use-credentials` is rejected too).
 *
 * SECURITY NOTE — product-accepted risk. This executes arbitrary third-party JS
 * first-party on our origin, from a CDN whose contents we do not control and the
 * vendor can change at any time. Guards: never inject it for an authenticated
 * user, and a runtime kill-switch (`retargetingEnabled`, surfaced via
 * /crash-config) that the admin panel or the script-integrity monitor can flip
 * off with no redeploy (see `shouldInjectPixel`). `teardownPixel` is best-effort
 * residue cleanup only — it removes the `<script>` node and the first-party
 * `_p_uid` cookie — and does NOT undo anything the script already executed
 * (event listeners, timers, injected scripts, hooked globals). It is not a
 * security boundary. A CDN-change monitor is tracked as separate follow-up work.
 *
 * Gating is expressed as a pure predicate (`shouldInjectPixel`) so it can be
 * unit-tested without a Nuxt/DOM harness; the plugin (`retargeting.client.ts`)
 * wires it to the auth store + route.
 */

export const PIXEL_ID = 'vk_aurumrun_io'
export const PIXEL_SRC = 'https://scripts.prdredir.com/scripts/vk_aurumrun_io.js'
const UID_COOKIE = '_p_uid'

export interface PixelGateState {
  /** True once the auth-restore attempt has settled (userStore.authInitialized).
   *  Until then a returning user may be painted optimistically, so "guest" is
   *  not yet decidable. */
  authInitialized: boolean
  /** True when a user is signed in (userStore.user !== null). */
  authenticated: boolean
  /** Current route path. */
  path: string
  /** The runtime kill-switch: `marketing.retargetingEnabled` from /crash-config
   *  (admin-flippable AND auto-disabled by the script-integrity monitor on
   *  drift). Fail-open — only an explicit `false` blocks injection. */
  retargetingEnabled: boolean
}

/**
 * The pixel fires for a settled anonymous visitor on the home route — on EVERY
 * such visit (no once-per-browser cap) — unless the runtime kill-switch is off.
 * Pure — no side effects, no globals.
 */
export function shouldInjectPixel(s: PixelGateState): boolean {
  return s.authInitialized && !s.authenticated && s.path === '/' && s.retargetingEnabled !== false
}

/**
 * Append the vendor-CDN pixel `<script>` — plain and credentialed (NO
 * `crossorigin`/`integrity`, so the vendor's `slfp` cookie is set on the fetch).
 * No-op if it is already in the DOM, so it injects at most once per page load; a
 * fresh load (reload / new visit) re-fires. The caller owns the guest / route
 * gate (`shouldInjectPixel`).
 */
export function injectPixel(): void {
  if (typeof document === 'undefined') return
  if (document.getElementById(PIXEL_ID)) return
  const s = document.createElement('script')
  s.id = PIXEL_ID
  s.src = PIXEL_SRC
  s.async = true
  ;(document.body ?? document.head ?? document.documentElement).appendChild(s)
}

/**
 * Best-effort cleanup of the residue the pixel leaves on OUR side: the
 * `<script>` node and the first-party `_p_uid` cookie it sets. Does NOT stop
 * anything the vendor script already executed (see the security note above) —
 * it is not a security boundary. Idempotent — safe to call for an authenticated
 * user who never had it injected.
 */
export function teardownPixel(): void {
  if (typeof document === 'undefined') return
  document.getElementById(PIXEL_ID)?.remove()
  // The pixel sets `_p_uid` host-only with `path=/` (no explicit domain), so a
  // matching `path=/` expiry in the past clears it.
  document.cookie = `${UID_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
}
